import React from 'react';
import {
    ConfirmedPaymentMethodResponseDto,
    OrderResponseDto,
} from 'client/widget-components/ecom/types/EcomSDK';
import {
    OrderViewSection,
    OrderViewSectionTitle,
    OrderViewDataCardStack,
    OrderViewPaymentInstructions,
    OrderViewPaymentNoticeWrapper,
} from '../styles';
import DataCard, { DataRow } from '../components/DataCard';
import WarningNotice from '../components/WarningNotice';
import {
    Bank,
    CardAmex,
    CardDiscover,
    CardInterac,
    CardMastercard,
    CardPaypal,
    CardVisa,
    Coin,
    CreditCard,
    MapPin,
    Money,
    Storefront,
    Wallet,
} from '../components/icons';
import {
    customFieldsToDataRows,
    formatPersonalAddress,
    getCustomFieldsByZone,
    getPaymentMethodDisplayName,
    paymentMethodRowIsLoading,
    withSeparatorIfNonEmpty,
} from '../utils';
import { shouldAutoRefresh } from '../useOrder';
import { useEcommStr } from '../useEcommStr';

function getConfirmedPaymentMethod(
    order: OrderResponseDto
): ConfirmedPaymentMethodResponseDto | undefined {
    if (!order.payment) return undefined;
    if (order.payment.confirmed_method.name === 'UNKNOWN') return undefined;
    return order.payment.confirmed_method;
}

function getPaymentMethodIcon(
    method: ConfirmedPaymentMethodResponseDto | undefined
): React.ReactNode {
    switch (method?.icon_name) {
        case 'CARD_VISA':
            return <CardVisa />;
        case 'CARD_MASTERCARD':
            return <CardMastercard />;
        case 'CARD_DISCOVER':
            return <CardDiscover />;
        case 'CARD_AMEX':
            return <CardAmex />;
        case 'CARD_INTERAC':
            return <CardInterac />;
        case 'PAYPAL':
            return <CardPaypal />;
        case 'CARD_UNKNOWN':
            return <CreditCard />;
        case 'MANUAL':
            return <Storefront />;
        case 'CASH':
            return <Money />;
        case 'CRYPTO':
            return <Coin />;
        case 'WALLET':
            return <Wallet />;
        case 'BANK_TRANSFER':
        case 'BANK_REDIRECT':
        case 'BANK_DEBIT':
            return <Bank />;
        default:
            return <CreditCard />;
    }
}

interface PaymentSectionProps {
    order: OrderResponseDto;
    isRefreshing: boolean;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({
    order,
    isRefreshing,
}) => {
    const str = useEcommStr();
    const paymentMethod = getConfirmedPaymentMethod(order);
    const isPending = order.payment_status === 'PENDING';
    const shouldRefresh = shouldAutoRefresh(order);
    const fieldsByZone = getCustomFieldsByZone(order);

    const pendingNoticeKey = paymentMethod?.instructions
        ? 'nativeEcommRuntime.order.pending_payment_notice'
        : 'nativeEcommRuntime.order.pending_payment_notice_no_instructions';

    const billingRows: DataRow[] = [
        { key: 'title', label: str('nativeEcommRuntime.billing.address') },
        {
            key: 'address',
            value: formatPersonalAddress(str, order.billing_address),
        },
        ...withSeparatorIfNonEmpty(
            customFieldsToDataRows(fieldsByZone.BILLING_ADDRESS)
        ),
    ];

    const isNameLoading = paymentMethodRowIsLoading('name', {
        hasPaymentMethod: !!paymentMethod,
        isRefreshing,
        shouldRefresh,
    });
    const isDetailsLoading = paymentMethodRowIsLoading('details', {
        hasPaymentMethod: !!paymentMethod,
        isRefreshing,
        shouldRefresh,
    });

    const methodRows: DataRow[] = [
        { key: 'title', label: str('nativeEcommRuntime.paymentMethod.title') },
    ];

    if (isNameLoading) {
        methodRows.push({ key: 'name', isLoading: true });
    } else if (paymentMethod) {
        methodRows.push({
            key: 'name',
            value: getPaymentMethodDisplayName(str, paymentMethod),
        });
    }

    if (paymentMethod?.details) {
        methodRows.push({ key: 'details', value: paymentMethod.details });
    } else if (isDetailsLoading) {
        methodRows.push({
            key: 'details',
            isLoading: true,
            loadingLabelWidth: '100%',
            loadingValueWidth: '100%',
        });
    }

    methodRows.push(
        ...withSeparatorIfNonEmpty(
            customFieldsToDataRows(fieldsByZone.PAYMENT_METHODS)
        )
    );

    return (
        <OrderViewSection>
            <OrderViewSectionTitle>
                {str('nativeEcommRuntime.payment.title')}
            </OrderViewSectionTitle>

            {paymentMethod && isPending && (
                <OrderViewPaymentNoticeWrapper>
                    <WarningNotice
                        level='warning'
                        text={str(pendingNoticeKey)}
                    />
                </OrderViewPaymentNoticeWrapper>
            )}

            <OrderViewDataCardStack>
                <DataCard
                    rows={billingRows}
                    dataAuto='order-billing-address'
                    icon={<MapPin />}
                />
                <DataCard
                    rows={methodRows}
                    dataAuto='order-payment-method'
                    icon={getPaymentMethodIcon(paymentMethod)}
                    iconUrl={paymentMethod?.icon_url}
                >
                    {paymentMethod?.instructions && (
                        <OrderViewPaymentInstructions
                            // Backend-controlled content; same XSS surface as Vue's v-html.
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: paymentMethod.instructions,
                            }}
                        />
                    )}
                </DataCard>
            </OrderViewDataCardStack>
        </OrderViewSection>
    );
};

export default PaymentSection;
